import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const [panelPath, setPanelPath] = useState(null);

  // Detecta login ao trocar de rota (cobre redirect pós-login)
  useEffect(() => {
    setIsMobileMenuOpen(false);
    const isAdmin   = !!sessionStorage.getItem('admin_token');
    const isStudent = !!localStorage.getItem('student_token');
    setLoggedIn(isAdmin || isStudent);
    setPanelPath(isAdmin ? '/admin' : isStudent ? '/area' : null);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('student_token');
    localStorage.removeItem('student_user');
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_email');
    setLoggedIn(false);
    navigate('/');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Sobre', path: '/sobre' },
    { name: 'Protocolos', path: '/consultas' },
    { name: 'Contato', path: '/contato' },
  ];

  return (
    <>
      <nav
        className={`sticky top-0 z-50 w-full transition-all duration-300 ${
          isScrolled
            ? 'bg-[#0A0A0A]/98 backdrop-blur-md shadow-[0_2px_20px_rgba(0,0,0,0.6)]'
            : 'bg-[#0A0A0A] '
        } border-b border-white/8`}
        data-testid="main-navigation"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">

            {/* Logo */}
            <Link
              to="/"
              data-editor-key="identity.nameUpper"
              className="flex items-center gap-3 group"
              data-testid="nav-logo"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <img
                src="/images/logo-symbol.png"
                alt="Daniel Aron"
                className="w-auto group-hover:opacity-80 transition-opacity"
                style={{ height: '37px' }}
              />
              <div className="leading-tight">
                <div className="font-bold text-base tracking-tight text-white group-hover:text-[#C0C0C0] transition-colors">
                  Daniel Aron
                </div>
                <div className="text-[10px] text-[#A3A3A3] tracking-[0.2em] uppercase hidden sm:block">
                  Nutricionista & Personal
                </div>
              </div>
            </Link>

            {/* Desktop Links */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={`text-sm font-medium tracking-widest uppercase transition-colors relative py-1 ${
                    location.pathname === link.path
                      ? 'text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-white'
                      : 'text-[#A3A3A3] hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              {loggedIn && panelPath && (
                <>
                  <div className="w-px h-4 bg-white/15" />
                  <Link
                    to={panelPath}
                    className={`text-sm font-medium tracking-widest uppercase transition-colors relative py-1 ${
                      location.pathname === panelPath
                        ? 'text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-white'
                        : 'text-[#A3A3A3] hover:text-white'
                    }`}
                  >
                    Painel
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-sm tracking-widest uppercase text-[#555] hover:text-white transition-colors duration-200"
                  >
                    Sair
                  </button>
                </>
              )}
              {!loggedIn && (
                <>
                  <div className="w-px h-4 bg-white/15" />
                  <Link
                    to="/login"
                    className={`text-sm font-medium tracking-widest uppercase transition-colors relative py-1 ${
                      location.pathname === '/login'
                        ? 'text-white after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-white'
                        : 'text-[#A3A3A3] hover:text-white'
                    }`}
                  >
                    Entrar
                  </Link>
                </>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden text-white p-2 -mr-2"
              data-testid="mobile-menu-button"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed top-20 left-0 right-0 z-40 bg-[#0A0A0A] border-b border-white/10 shadow-2xl"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col px-6 py-6 gap-5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  className={`text-xl font-semibold uppercase tracking-wide transition-colors ${
                    location.pathname === link.path
                      ? 'text-white'
                      : 'text-[#A3A3A3]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              {loggedIn && panelPath && (
                <Link
                  to={panelPath}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-xl font-semibold uppercase tracking-wide text-[#A3A3A3]"
                >
                  Painel
                </Link>
              )}
              <button
                onClick={() => { setIsMobileMenuOpen(false); loggedIn ? handleLogout() : navigate('/login'); }}
                className="text-xl font-semibold uppercase text-white border border-white/30 py-3 hover:bg-white hover:text-black transition-colors mt-2"
              >
                {loggedIn ? 'Sair' : 'Entrar'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
